import { createContext, useContext, type ReactNode } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { authApi } from '@/api/auth';
import { queryKeys } from '@/lib/queryKeys';
import type { User, Organization } from '@/api/types';

interface AuthContextValue {
  user: User | null;
  organizations: Organization[];
  isLoading: boolean;
  isAuthenticated: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string, name: string) => Promise<string>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: queryKeys.auth.me,
    queryFn: () => authApi.me(),
    retry: false,
  });

  const loginMutation = useMutation({
    mutationFn: (input: { email: string; password: string }) => authApi.login(input),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.auth.me }),
  });

  const registerMutation = useMutation({
    mutationFn: (input: { email: string; password: string; name: string }) => authApi.register(input),
  });

  const logoutMutation = useMutation({
    mutationFn: () => authApi.logout(),
    onSuccess: () => {
      qc.setQueryData(queryKeys.auth.me, null);
      qc.clear();
    },
  });

  const meData = data as { user?: User; organizations?: Organization[] } | null | undefined;
  const organizations = meData?.organizations ?? [];
  const rawUser = meData?.user ?? null;

  // Ensure orgId is populated — fall back to first org if JWT had empty orgId
  const user = rawUser && (!rawUser.orgId && organizations.length > 0)
    ? { ...rawUser, orgId: organizations[0]!.id, orgRole: 'owner' }
    : rawUser;

  const value: AuthContextValue = {
    user,
    organizations,
    isLoading,
    isAuthenticated: !!user,
    login: async (email, password) => {
      await loginMutation.mutateAsync({ email, password });
    },
    register: async (email, password, name) => {
      const result = await registerMutation.mutateAsync({ email, password, name });
      return result.message;
    },
    logout: async () => {
      await logoutMutation.mutateAsync();
    },
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
}
